import { processProgress, type PProcessEvidence, type PProcessProgress, type PProcessStep } from "./pProcess";
import type { PiSettlement } from "./piProgress";

/**
 * Quy trình thường — đơn KHÔNG chạy quy trình P đang ở bước nào (Roary qua anh Toàn 29/10/2026: "một màn hình riêng là một quy trình
 * bình thường … cũng phải hiển thị từng bước như vậy, để người dùng có thể kiểm tra dễ dàng").
 *
 * Khách CHƯA đưa tờ quy trình như tờ "P 流程", nên chuỗi bước chốt theo chuẩn bán hàng → thu tiền (SAP order-to-cash, Odoo Sales →
 * Inventory → Invoicing), anh Toàn đồng ý 29/10/2026 — khách bác lại được (FLOW-R01; màn không hiện nhãn, anh Toàn tự hỏi khách). Mọi bước
 * đọc thẳng từ chứng từ đã có trong sổ, không có ô tick tay — như quy trình P.
 *
 * Dạng KHAI BÁO, cùng bộ giải với quy trình P (`processProgress`): thêm bớt bước, đổi thứ tự hay đổi chứng từ nào tính là xong đều
 * sửa đúng mảng này.
 *
 * Bản 1.1.81 bắt đủ tám bước, nên trên production mọi đơn đều "chờ đơn bán hàng" và phần lớn "làm nhảy cóc" — nhân viên đi
 * PI → hóa đơn → thu tiền. Anh Toàn duyệt 29/10/2026 (khách vẫn bác lại được, FLOW-R01):
 *  - đơn bán hàng và phiếu đóng gói TÙY CHỌN — với hàng thương mại PI đã là chứng từ khách xác nhận đơn (trang KPI cũng không đòi
 *    hai mốc này, `piProgress.ts`); đơn tự sản xuất vẫn luôn có đơn bán hàng vì lệnh sản xuất chỉ gắn được PI qua đơn bán hàng;
 *  - phiếu giao BẮT BUỘC — giao hàng là xuất kho (`deliveryFulfillment.ts`, 18/09/2026): không lập phiếu giao thì tồn kho không
 *    bao giờ trừ phần hàng đã bán;
 *  - thu tiền vẫn là bước cuối nhưng thu trước khi giao (trả trước theo PI) không tính nhảy cóc.
 */
export const ORDER_PROCESS_STEPS: readonly PProcessStep[] = [
  { code: "piIssued", seq: 1, applies: "always", evidence: "ledger", carries: ["pi"] },
  { code: "salesOrder", seq: 2, applies: "always", optional: true, evidence: "ledger", carries: ["pi"] },
  // Nguồn hàng rẽ nhánh: phải mua (đơn mua gắn PI) và / hoặc tự sản xuất (lệnh sản xuất gắn PI). Không nhánh nào = hàng có sẵn trong
  // kho, hai nấc này coi như qua — đơn có sẵn hàng không được đứng mãi ở "chờ đơn mua".
  { code: "purchaseOrder", seq: 3, applies: "purchased", evidence: "ledger", carries: ["pi"] },
  { code: "productionOrder", seq: 3, applies: "produced", evidence: "ledger", carries: ["pi"] },
  { code: "goodsReceipt", seq: 4, applies: "purchased", evidence: "ledger", carries: ["lot"] },
  { code: "productionOutput", seq: 4, applies: "produced", evidence: "ledger", carries: ["lot"] },
  { code: "packing", seq: 5, applies: "always", optional: true, evidence: "ledger", carries: ["pi"] },
  { code: "invoice", seq: 6, applies: "always", evidence: "ledger", carries: ["pi"] },
  { code: "delivery", seq: 7, applies: "always", evidence: "ledger", carries: ["pi"] },
  { code: "payment", seq: 8, applies: "always", anyOrder: true, evidence: "ledger", carries: ["pi"] },
];

/** Chứng từ đọc được cho MỘT PI — tầng nghiệp vụ đổi thành danh sách bước đã xong (`orderProcessDone`). */
export interface OrderProcessEvidence {
  salesOrder: boolean;
  purchased: boolean;
  produced: boolean;
  goodsReceipt: boolean;
  productionOutput: boolean;
  packing: boolean;
  invoice: boolean;
  /** Có phiếu giao chưa huỷ (hàng đã lên đường) — chưa tính là xong. */
  deliveryStarted: boolean;
  /** Có phiếu giao ở trạng thái Delivered — khách đã nhận. */
  delivered: boolean;
  settlement: PiSettlement;
}

/** Bước đã xong — giữ riêng để kiểm được: đây là chỗ diễn giải sổ sách thành công đoạn. */
export function orderProcessDone(e: OrderProcessEvidence): string[] {
  const done = ["piIssued"];
  if (e.salesOrder) done.push("salesOrder");
  if (e.purchased) done.push("purchaseOrder");
  if (e.produced) done.push("productionOrder");
  if (e.goodsReceipt) done.push("goodsReceipt");
  if (e.productionOutput) done.push("productionOutput");
  if (e.packing) done.push("packing");
  if (e.invoice) done.push("invoice");
  if (e.delivered) done.push("delivery");
  // Thu một phần chưa phải xong: bước cuối chỉ sáng khi đã thu đủ.
  if (e.settlement === "paid") done.push("payment");
  return done;
}

export function orderProcessProgress(e: OrderProcessEvidence): PProcessProgress {
  const evidence: PProcessEvidence = { purchased: e.purchased, subcontracted: false, produced: e.produced, done: orderProcessDone(e) };
  return processProgress(ORDER_PROCESS_STEPS, evidence);
}

/** Một dòng trên màn Quy trình thường — một PI. */
export interface OrderProcessRow extends PProcessProgress {
  piId: string;
  piNo: string;
  customerName: string | null;
  date: number | null;
  purchased: boolean;
  produced: boolean;
  /** Phiếu giao đã lập nhưng khách chưa nhận — để nói rõ ở bước giao hàng. */
  deliveryStarted: boolean;
  /** Đã thu tới đâu — CHỈ trạng thái, không con số. */
  settlement: PiSettlement;
}
